import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { logger } from '../utils/logger';

const DEFAULT_CONTEXT = {
  tickers: {},
  loading: true,
  error: null,
  stale: false,
  updatedAt: null,
  source: null,
  refresh: () => {}
};

export const CryptoMarketContext = createContext(DEFAULT_CONTEXT);

// Transform the backend's `{ SYMBOL: snapshot }` shape to the `{ SYMBOL/USD:
// snapshot }` keyed map existing consumers (DashboardPage, PortfolioAnalytics,
// MarketStatsBar) already depend on.
//
// A missing/unparseable numeric field stays `null` — NOT 0. Collapsing an
// unavailable price to 0 fabricates a $0 reading that downstream valuation
// (getUSDPrice) and price displays cannot distinguish from a real zero. Honest
// "unknown" must survive the transform so consumers can render "--"/skeleton.
const transformBackendTickers = (rawTickers = {}) => {
  const nullableNumber = (value) => {
    // null/undefined/'' are all "unavailable" -> null. The empty-string guard
    // matters because Number('') is 0, which would otherwise fabricate a $0 for
    // a backend field sent as an empty string.
    if (value === null || value === undefined || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  return Object.entries(rawTickers).reduce((acc, [symbol, payload]) => {
    if (!symbol || !payload) return acc;
    acc[`${symbol}/USD`] = {
      price: nullableNumber(payload.price),
      change24h: nullableNumber(payload.change24h),
      changePercent24h: nullableNumber(payload.changePercent24h),
      volume24h: nullableNumber(payload.volume24h),
      marketCap: nullableNumber(payload.marketCap)
    };
    return acc;
  }, {});
};

export const CryptoMarketProvider = ({ children, pollInterval = 30000 }) => {
  const [tickers, setTickers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  // Provenance of the rendered data (e.g. "cryptocompare"). Surfaced so
  // consumers can honestly label external/reference data rather than
  // presenting it as own-exchange truth.
  const [source, setSource] = useState(null);
  const pollRef = useRef(null);

  // Hits the backend ticker proxy. The old direct-CryptoCompare path burned
  // provider quota per browser/tab and surfaced provider rate-limit errors
  // as a total-failure banner. The backend now owns TTL caching, stale-data
  // fallbacks, and API-key handling; we just render whatever it hands back.
  //
  // The endpoint is public — the ticker is rendered on the unauthenticated
  // marketing home page too — so we don't attach an access token here. If the
  // user is authenticated we still fetch; if they're not, we still fetch.
  const fetchMarketData = useCallback(async () => {
    try {
      setError(null);

      const response = await axios.get(
        `${getApiUrl('javaBackend')}/api/v1/market/external/tickers`
      );

      const payload = response?.data || {};
      const backendTickers = payload.tickers || {};
      setTickers(transformBackendTickers(backendTickers));
      setStale(Boolean(payload.stale));
      setUpdatedAt(payload.updatedAt || null);
      setSource(payload.source || null);
    } catch (err) {
      // 503 = backend had no cache and provider was down. Keep whatever
      // tickers we already had and just flag stale so the UI can show a
      // subtle degraded indicator instead of wiping to a red banner.
      if (err?.response?.status === 503 && err?.response?.data) {
        const payload = err.response.data;
        const backendTickers = payload.tickers || {};
        const mapped = transformBackendTickers(backendTickers);
        if (Object.keys(mapped).length > 0) {
          setTickers(mapped);
        }
        setStale(true);
        setUpdatedAt(payload.updatedAt || null);
        setSource(payload.source || null);
        setError(null);
      } else {
        logger.warn('Failed to fetch market data:', err.message);
        setStale(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Fetch unconditionally — the endpoint is public, and the Auth0 loading
    // state isn't a gate for market banner data.
    fetchMarketData();

    if (pollInterval > 0) {
      pollRef.current = setInterval(fetchMarketData, pollInterval);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, [fetchMarketData, pollInterval]);

  const value = useMemo(
    () => ({
      tickers,
      loading,
      error,
      stale,
      updatedAt,
      source,
      refresh: fetchMarketData
    }),
    [tickers, loading, error, stale, updatedAt, source, fetchMarketData]
  );

  return (
    <CryptoMarketContext.Provider value={value}>
      {children}
    </CryptoMarketContext.Provider>
  );
};

export const useCryptoMarketData = () => {
  const context = useContext(CryptoMarketContext);
  if (!context) {
    throw new Error('useCryptoMarketData must be used within a CryptoMarketProvider');
  }
  return context;
};
